import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Shield, Users as UsersIcon, Plus, Pencil, KeyRound, Ban, Trash2,
  Eye, EyeOff, Wand2, CheckCircle2,
} from "lucide-react";
import {
  adminListUsers, adminCreateUser, adminPatchUser,
  adminResetUserPassword, adminDeleteUser, adminGeneratePassword,
} from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Vollzugriff inkl. User-Verwaltung", color: "bg-tot/10 text-tot border-tot/30" },
  { value: "user", label: "User", desc: "Standard: CRM + Datenraum + Vermietung", color: "bg-navy/10 text-navy border-navy/30" },
  { value: "viewer", label: "Viewer", desc: "Nur Lesezugriff, kein Editieren", color: "bg-gold/10 text-[#8B6914] border-gold/40" },
];

const validatePw = (pw) => {
  const rules = [
    { test: (p) => p.length >= 10, msg: "min. 10 Zeichen" },
    { test: (p) => /[a-z]/.test(p), msg: "Kleinbuchstabe" },
    { test: (p) => /[A-Z]/.test(p), msg: "Großbuchstabe" },
    { test: (p) => /\d/.test(p), msg: "Ziffer" },
    { test: (p) => /[^a-zA-Z0-9]/.test(p), msg: "Sonderzeichen" },
  ];
  return rules.map((r) => ({ ...r, ok: r.test(pw || "") }));
};

function RoleBadge({ rolle }) {
  const r = ROLES.find((x) => x.value === rolle) || ROLES[1];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widish border ${r.color}`}>
      {rolle === "admin" && <Shield size={10} />}
      {r.label}
    </span>
  );
}

function StatusPill({ aktiv }) {
  if (aktiv === false) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widish bg-rose-50 text-rose-800 border border-rose-200"><Ban size={10} /> inaktiv</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widish bg-emerald-50 text-emerald-800 border border-emerald-200"><CheckCircle2 size={10} /> aktiv</span>;
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwResetUser, setPwResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListUsers();
      setUsers(d.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const softDelete = async (u) => {
    if (!window.confirm(`Zugang von ${u.name} deaktivieren?`)) return;
    try {
      await adminDeleteUser(u.id, false);
      toast.success(`${u.name} deaktiviert`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const hardDelete = async (u) => {
    const c = window.prompt(`Endgültig löschen? Tippe zur Bestätigung: LÖSCHEN`);
    if (c !== "LÖSCHEN") return;
    try {
      await adminDeleteUser(u.id, true);
      toast.success(`${u.name} endgültig gelöscht`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="admin-users-page" className="pb-container px-4 lg:px-8 py-8 space-y-6">
      <PageHeader
        eyebrow="System · Admin"
        title="Benutzer"
        subtitle="Benutzer, Rollen und Zugänge verwalten. Nur Admins sehen diese Seite."
        testid="admin-users-header"
        actions={
          <>
            <button onClick={load} className="pb-btn-outline text-xs uppercase tracking-widish gap-2 min-h-[40px]" data-testid="admin-users-reload">
              <RefreshCw size={12} /> <span className="hidden sm:inline">Neu laden</span>
            </button>
            <button onClick={() => setCreateOpen(true)} className="pb-btn-primary text-xs uppercase tracking-widish gap-2 min-h-[40px]" data-testid="admin-users-new-btn">
              <Plus size={13} /> Neuer Benutzer
            </button>
          </>
        }
      />

      {loading ? (
        <div className="text-navy/55 font-sans">Lade …</div>
      ) : (
        <div className="border border-navy/12 bg-white overflow-x-auto">
          <table className="w-full text-sm font-sans" data-testid="admin-users-table">
            <thead className="text-navy/60 uppercase text-[10px] tracking-widish border-b border-navy/10">
              <tr>
                <th className="text-left px-3 py-2.5 font-normal">Benutzername</th>
                <th className="text-left px-3 py-2.5 font-normal">Name</th>
                <th className="text-left px-3 py-2.5 font-normal">E-Mail</th>
                <th className="text-left px-3 py-2.5 font-normal">Rolle</th>
                <th className="text-left px-3 py-2.5 font-normal">Status</th>
                <th className="text-left px-3 py-2.5 font-normal">Erstellt</th>
                <th className="text-left px-3 py-2.5 font-normal">Letzter Login</th>
                <th className="text-right px-3 py-2.5 font-normal">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-navy/45 font-sans">Keine Benutzer vorhanden.</td>
                </tr>
              )}
              {users.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-b border-navy/8 last:border-b-0 hover:bg-navy/[0.02]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-navy">{u.username || <span className="text-navy/30">—</span>}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-navy">{u.name}</span>
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widish text-gold">Sie</span>}
                    </td>
                    <td className="px-3 py-2.5 text-navy/75 text-xs">{u.email || <span className="text-navy/30">—</span>}</td>
                    <td className="px-3 py-2.5"><RoleBadge rolle={u.rolle} /></td>
                    <td className="px-3 py-2.5"><StatusPill aktiv={u.aktiv} /></td>
                    <td className="px-3 py-2.5 text-navy/60 text-xs tabular-nums">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-navy/60 text-xs tabular-nums">
                      {u.letzter_login ? new Date(u.letzter_login).toLocaleString("de-DE") : <span className="text-navy/30">nie</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setEditUser(u)}
                          title="Bearbeiten"
                          data-testid={`admin-user-edit-${u.id}`}
                          className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => setPwResetUser(u)}
                          title="Passwort zurücksetzen"
                          data-testid={`admin-user-resetpw-${u.id}`}
                          className="p-1.5 text-navy/60 hover:text-gold hover:bg-gold/10 border border-navy/15"
                        >
                          <KeyRound size={12} />
                        </button>
                        {u.aktiv !== false && !isSelf && (
                          <button
                            onClick={() => softDelete(u)}
                            title="Deaktivieren (soft)"
                            data-testid={`admin-user-deactivate-${u.id}`}
                            className="p-1.5 text-navy/60 hover:text-white hover:bg-navy border border-navy/15"
                          >
                            <Ban size={12} />
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => hardDelete(u)}
                            title="Endgültig löschen"
                            data-testid={`admin-user-delete-${u.id}`}
                            className="p-1.5 text-tot/70 hover:text-white hover:bg-tot border border-tot/30"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-navy/55 font-sans">
        <UsersIcon size={12} className="inline mr-1" />
        Beim ersten Login werden neue Benutzer aufgefordert, das temporäre Passwort zu ändern.
      </p>

      {createOpen && (
        <CreateUserModal onClose={() => setCreateOpen(false)} onCreated={() => { load(); setCreateOpen(false); }} />
      )}
      {editUser && (
        <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { load(); setEditUser(null); }} />
      )}
      {pwResetUser && (
        <ResetPasswordModal user={pwResetUser} onClose={() => setPwResetUser(null)} />
      )}
    </div>
  );
}

// ---------------------- Create User Modal ----------------------
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    username: "", name: "", email: "", rolle: "user", initial_password: "",
    betreuer_intern_mapping: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const rules = validatePw(form.initial_password);
  const allOk = rules.every((r) => r.ok);

  const generate = async () => {
    try {
      const d = await adminGeneratePassword();
      setForm((f) => ({ ...f, initial_password: d.password }));
      setShowPw(true);
      toast.success("Zufallspasswort generiert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generieren fehlgeschlagen"); }
  };

  const submit = async () => {
    if (!form.username.trim() || !form.name.trim()) return toast.error("Benutzername + Name erforderlich");
    if (!allOk) return toast.error("Passwort erfüllt nicht die Regeln");
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || undefined };
      const r = await adminCreateUser(payload);
      toast.success(`Benutzer ${r.user.name} angelegt`);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-40" />
      <div data-testid="create-user-modal" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,95vw)] max-h-[95vh] bg-white border border-navy/15 z-50 flex flex-col">
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Neuer Benutzer</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">Benutzer anlegen</h3>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="pb-label">Benutzername <span className="text-tot">*</span></label>
            <input
              data-testid="create-user-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              placeholder="z.B. marion.mueller"
              className="pb-input--full"
              autoFocus
            />
            <p className="text-[10px] text-navy/45 mt-1">Nur Kleinbuchstaben, Zahlen, `_` und `-`</p>
          </div>
          <div>
            <label className="pb-label">Voller Name <span className="text-tot">*</span></label>
            <input data-testid="create-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Marion Müller" className="pb-input--full" />
          </div>
          <div>
            <label className="pb-label">E-Mail</label>
            <input data-testid="create-user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="marion@heinrichs.group" className="pb-input--full" />
          </div>
          <div>
            <label className="pb-label">Rolle</label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="create-user-role-picker">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, rolle: r.value })}
                  className={`px-2 py-2 border text-left text-xs ${form.rolle === r.value ? "border-gold bg-gold/5" : "border-navy/15 hover:border-navy/30"}`}
                >
                  <div className="font-medium text-navy">{r.label}</div>
                  <div className="text-[10px] text-navy/55 mt-0.5">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="pb-label flex items-center justify-between">
              Initial-Passwort <span className="text-tot">*</span>
              <button type="button" onClick={generate} className="text-[10px] text-gold hover:underline inline-flex items-center gap-1" data-testid="create-user-generate-pw-btn">
                <Wand2 size={10} /> Zufällig generieren
              </button>
            </label>
            <div className="relative">
              <input
                data-testid="create-user-password-input"
                type={showPw ? "text" : "password"}
                value={form.initial_password}
                onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                className="pb-input--full pr-10 font-mono text-sm"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/50 hover:text-navy">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <ul className="mt-1.5 grid grid-cols-2 gap-x-3 text-[10px] font-sans" data-testid="create-user-pw-rules">
              {rules.map((r, i) => (
                <li key={i} className={r.ok ? "text-emerald-700" : "text-navy/40"}>
                  {r.ok ? "✓" : "○"} {r.msg}
                </li>
              ))}
            </ul>
          </div>
          <div className="text-[11px] text-navy/55 border-l-2 border-gold pl-2 py-1 bg-gold/5">
            Der Benutzer muss beim ersten Login das temporäre Passwort ändern.
          </div>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost" disabled={saving}>Abbrechen</button>
          <button onClick={submit} className="pb-btn-primary" disabled={saving || !allOk} data-testid="create-user-submit">
            {saving ? "…" : "Anlegen"}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------- Edit User Modal ----------------------
function EditUserModal({ user, onClose, onSaved }) {
  const { user: me } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    rolle: user.rolle || "user",
    aktiv: user.aktiv !== false,
  });
  const [saving, setSaving] = useState(false);
  const isSelf = me?.id === user.id;

  const submit = async () => {
    setSaving(true);
    try {
      await adminPatchUser(user.id, form);
      toast.success("Änderungen gespeichert");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-40" />
      <div data-testid="edit-user-modal" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-50">
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Bearbeiten</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">{user.name}</h3>
          <p className="text-[10px] text-navy/45 font-mono mt-0.5">{user.username}</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="pb-label">Voller Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pb-input--full" data-testid="edit-user-name-input" />
          </div>
          <div>
            <label className="pb-label">E-Mail</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pb-input--full" data-testid="edit-user-email-input" />
          </div>
          <div>
            <label className="pb-label">Rolle</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  disabled={isSelf && r.value !== "admin"}
                  onClick={() => setForm({ ...form, rolle: r.value })}
                  className={`px-2 py-2 border text-left text-xs disabled:opacity-40 disabled:cursor-not-allowed ${form.rolle === r.value ? "border-gold bg-gold/5" : "border-navy/15 hover:border-navy/30"}`}
                >
                  <div className="font-medium text-navy">{r.label}</div>
                  <div className="text-[10px] text-navy/55 mt-0.5">{r.desc}</div>
                </button>
              ))}
            </div>
            {isSelf && <p className="text-[10px] text-tot mt-1">Sie können Ihre eigene Rolle nicht ändern.</p>}
          </div>
          <label className={`flex items-center gap-2 text-sm text-navy font-sans ${isSelf ? "opacity-40" : ""}`}>
            <input
              type="checkbox"
              checked={form.aktiv}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
              className="accent-[#022750]"
              data-testid="edit-user-aktiv-checkbox"
            />
            Aktiv (Login erlaubt)
            {isSelf && <span className="text-[10px] text-tot ml-2">Sie können sich nicht selbst sperren.</span>}
          </label>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
          <button onClick={submit} className="pb-btn-primary" disabled={saving} data-testid="edit-user-submit">
            {saving ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------- Reset Password Modal ----------------------
function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(true);
  const [saving, setSaving] = useState(false);
  const rules = validatePw(pw);
  const allOk = rules.every((r) => r.ok);

  const generate = async () => {
    try { const d = await adminGeneratePassword(); setPw(d.password); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const submit = async () => {
    if (!allOk) return toast.error("Passwort erfüllt nicht die Regeln");
    setSaving(true);
    try {
      await adminResetUserPassword(user.id, pw);
      toast.success(`Passwort zurückgesetzt für ${user.name}`);
      // Bitte Passwort dem User sicher übergeben
      window.prompt(`Neues Passwort für ${user.name} — bitte sicher übergeben:`, pw);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-40" />
      <div data-testid="reset-pw-modal" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-50">
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Passwort zurücksetzen</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">{user.name}</h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="pb-label flex items-center justify-between">
              Neues temporäres Passwort
              <button type="button" onClick={generate} className="text-[10px] text-gold hover:underline inline-flex items-center gap-1" data-testid="reset-pw-generate">
                <Wand2 size={10} /> Zufällig generieren
              </button>
            </label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="pb-input--full pr-10 font-mono text-sm" data-testid="reset-pw-input" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/50">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <ul className="mt-1.5 grid grid-cols-2 gap-x-3 text-[10px] font-sans">
              {rules.map((r, i) => (
                <li key={i} className={r.ok ? "text-emerald-700" : "text-navy/40"}>{r.ok ? "✓" : "○"} {r.msg}</li>
              ))}
            </ul>
          </div>
          <div className="text-[11px] text-navy/55 border-l-2 border-gold pl-2 py-1 bg-gold/5">
            Der Benutzer wird beim nächsten Login aufgefordert, das Passwort zu ändern.
          </div>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
          <button onClick={submit} className="pb-btn-primary" disabled={saving || !allOk} data-testid="reset-pw-submit">
            {saving ? "…" : "Zurücksetzen"}
          </button>
        </div>
      </div>
    </>
  );
}
